import React, { useEffect, useState } from "react";
import { Share, PlusSquare, X, Download } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Install prompt — nudges mobile users to install SoilHaul as a PWA.
 *
 * Behavior:
 *  - Hidden entirely if the app is already running in standalone (installed).
 *  - Hidden on desktop viewports (only useful on phones/tablets).
 *  - Hidden until the user is authenticated (avoids interrupting the login flow).
 *  - Dismissible; dismissal is remembered for 14 days in localStorage.
 *  - Android/Chrome path: captures `beforeinstallprompt`, shows an
 *    "Install" button that fires the native OS install sheet.
 *  - iOS Safari path: shows a static instruction card (Share → Add to Home Screen)
 *    since iOS doesn't support programmatic install.
 */

const DISMISS_KEY = "soilhaul.installPromptDismissedAt";
const DISMISS_DAYS = 14;
const INSTALLED_KEY = "soilhaul.installedAt";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari uses a proprietary flag rather than the standard media query
    window.navigator.standalone === true
  );
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(max-width: 820px)").matches;
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  if (isIOS && isSafari) return "ios-safari";
  if (isIOS) return "ios-other"; // Chrome/Firefox on iOS can't install PWAs — skip
  if (isAndroid) return "android";
  return "other";
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = new Date(raw).getTime();
    if (Number.isNaN(at)) return false;
    const days = (Date.now() - at) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const { user } = useAuth();
  const [platform] = useState(detectPlatform);
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Capture the Android/Chrome install event as soon as the browser fires it.
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, new Date().toISOString());
      setDeferredPrompt(null);
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Decide whether to actually show the banner. We wait ~4s after login so it
  // doesn't slam the user right as the dashboard is painting.
  useEffect(() => {
    if (!user) return;
    if (isStandalone()) return;
    if (!isMobileViewport()) return;
    if (wasRecentlyDismissed()) return;
    if (platform === "other" || platform === "ios-other") return;
    if (platform === "android" && !deferredPrompt) return; // wait for the event

    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, [user, platform, deferredPrompt]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (choice.outcome === "accepted") {
      setVisible(false);
      setDeferredPrompt(null);
    } else {
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pointer-events-none"
      data-testid="install-prompt-banner"
    >
      <div className="mx-auto max-w-md bg-gray-900 text-white shadow-2xl pointer-events-auto border-l-4 border-orange-500">
        <div className="flex items-start gap-3 p-4">
          <div className="w-10 h-10 bg-orange-500 flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-orange-400 font-bold">
              Field App
            </div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">
              Install SoilHaul
            </div>
            {platform === "ios-safari" ? (
              <p className="text-sm text-gray-300 mt-2 leading-snug">
                Tap <Share className="inline w-4 h-4 mx-0.5 -mt-0.5" /> below,
                then <span className="font-semibold text-white">Add to Home Screen</span>
                <PlusSquare className="inline w-4 h-4 ml-1 -mt-0.5" />
                {" "}to launch SoilHaul like a real app — no Safari URL bar.
              </p>
            ) : (
              <p className="text-sm text-gray-300 mt-2 leading-snug">
                Get a home-screen icon and faster launches. One tap.
              </p>
            )}
            <div className="flex items-center gap-2 mt-3">
              {platform === "android" && (
                <button
                  onClick={install}
                  data-testid="install-prompt-install-button"
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2 transition-colors"
                >
                  Install
                </button>
              )}
              <button
                onClick={dismiss}
                data-testid="install-prompt-dismiss-button"
                className="text-xs text-gray-400 hover:text-white uppercase tracking-widest px-3 py-2"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="install-prompt-close-button"
            className="text-gray-400 hover:text-white flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
